import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CampusHeader from '../../components/feature/CampusHeader';
import Footer from '../../components/feature/Footer';
import { subscribeAuth, getUserRole, signOutUser, signInWithEmail } from '../../services/auth';
import { sendWelcomeEmail } from '../../services/email';
import { getAllListings } from '../../services/listings'
import { auth } from '../../lib/firebase'
import type { ChangeEvent } from 'react';
import { toUserMessage } from '../../utils/errors';

const ProfilePage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userType, setUserType] = useState<'buyer' | 'seller' | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Check if user is logged in on component mount
  useEffect(() => {
    const unsub = subscribeAuth(async (user) => {
      const loggedIn = !!user;
      setIsLoggedIn(loggedIn);
      if (loggedIn && user) {
        const { role, status } = await getUserRole(user.uid);
        setUserType((role as 'buyer' | 'seller') ?? null);
        // Only sellers with pending status should see the pending verification page
        if (role === 'seller' && status === 'pending') {
          navigate('/pending-verification');
          return;
        }
        // Redirect to dashboards based on role
        if (role === 'buyer') {
          navigate('/buyer-dashboard');
          return;
        }
        if (role === 'seller') {
          navigate('/seller-dashboard');
          return;
        }
        setShowLoginModal(false);
      } else {
        setShowLoginModal(true);
      }
    });
    return () => unsub();
  }, [navigate]);

  const [activeListingsCount, setActiveListingsCount] = useState(0);
  
  useEffect(() => {
    if (userType !== 'seller') return;
    (async () => {
      const listings = await getAllListings();
      const current = auth.currentUser;
      const name = current?.displayName?.trim();
      const email = current?.email?.trim();
      const owned = listings.filter(l => {
        const seller = l.seller?.trim();
        if (!seller) return false;
        return (name && seller === name) || (email && seller === email);
      });
      setActiveListingsCount(owned.length);
    })();
  }, [userType]);
 
  // Mock user data - this would come from Supabase
  const userData = {
    buyer: {
      id: '1',
      name: 'John Doe',
      email: 'john.doe@university.edu',
      phone: '+234 123 456 7890',
      university: 'Obafemi Awolowo University (OAU)',
      joinedDate: 'January 2024',
      avatar: 'JD',
      favoriteItems: 12,
      purchaseHistory: 8,
      savedSearches: 5
    },
    seller: {
      id: '2',
      name: 'Sarah Chen',
      email: 'sarah.chen@university.edu',
      phone: '+234 987 654 3210',
      university: 'Federal University of Technology, Akure (FUTA)',
      joinedDate: 'March 2023',
      avatar: 'SC',
      isVerified: true,
      rating: 4.8,
      totalSales: 24,
      activeListings: 3,
      totalEarnings: 450000,
      responseTime: '< 1 hour'
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoginLoading(true);
    try {
      const user = await signInWithEmail(loginEmail.trim(), loginPassword.trim());
      try {
        const { role } = await getUserRole(user.uid);
        await sendWelcomeEmail(user.uid, user.email || loginEmail.trim(), user.displayName || undefined, role || 'buyer');
      } catch {}
      setShowLoginModal(false);
      // Navigate to profile after successful login
      navigate('/profile');
    } catch (err: any) {
      setLoginError(toUserMessage(err));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
    } finally {
      setIsLoggedIn(false);
      setUserType(null);
      navigate('/');
    }
  };

  // Login modal JSX is now inlined where it is rendered to keep a stable DOM and avoid input focus loss.

  // Not logged in state
  if (!isLoggedIn || showLoginModal) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        
        <main className="max-w-4xl mx-auto px-4 py-8 md:py-12">
          <div className="text-center">
            <div className="bg-white rounded-lg shadow-lg p-6 md:p-12">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-line text-2xl md:text-3xl text-gray-400"></i>
              </div>
              
              <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-4">
                Access Your Profile
              </h1>
              <p className="text-gray-600 mb-8 max-w-md mx-auto text-sm md:text-base">
                Login to view your profile, manage your listings, track your purchases, and access all marketplace features.
              </p>

              <button
                onClick={() => setShowLoginModal(true)}
                className="bg-teal-600 text-white px-6 md:px-8 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium whitespace-nowrap cursor-pointer text-sm md:text-base"
              >
                Sign In
              </button>
            </div>
          </div>
        </main>

        <Footer />
        {showLoginModal && (
          <div
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="login-modal-title"
          >
            <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
              <button
                aria-label="Close"
                onClick={() => setShowLoginModal(false)}
                className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition"
              >
                <i className="ri-close-line text-lg"></i>
              </button>

              <div className="bg-gradient-to-r from-teal-600 to-blue-600 px-6 py-5 text-white">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <i className="ri-user-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h2 id="login-modal-title" className="text-xl font-semibold">Welcome back</h2>
                    <p className="text-white/80 text-sm">Sign in to continue</p>
                  </div>
                </div>
              </div>

              <div className="px-6 py-6">
                <form onSubmit={handleLogin} className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setLoginEmail(e.target.value)}
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="your.email@university.edu"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="password"
                        value={loginPassword}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => setLoginPassword(e.target.value)}
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                        placeholder="Enter your password"
                        required
                      />
                    </div>
                  </div>

                  {loginError && (
                    <div className="flex items-center space-x-2 px-3 py-2 rounded-md bg-red-50 border border-red-100 text-red-700 text-sm">
                      <i className="ri-error-warning-line"></i>
                      <span>{loginError}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 text-white py-3 px-4 rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm disabled:opacity-60"
                  >
                    {loginLoading ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    onClick={() => navigate('/auth/sign-up')}
                    className="text-gray-700 hover:text-teal-700 font-medium cursor-pointer text-sm"
                  >
                    Don’t have an account? Sign up
                  </button>
                </div>

                <div className="mt-2 text-center">
                  <button
                    onClick={() => {
                      setShowLoginModal(false);
                      navigate('/');
                    }}
                    className="text-gray-500 hover:text-gray-700 cursor-pointer text-xs"
                  >
                    Continue browsing
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Buyer Profile
  if (userType === 'buyer') {
    const user = userData.buyer;
    
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        
        <main className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          {/* Profile Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-8 mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-lg md:text-2xl font-bold text-blue-600">{user.avatar}</span>
              </div>
              
              <div className="flex-1">
                <h1 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">{user.name}</h1>
                <p className="text-gray-600 mb-2 text-sm md:text-base">{user.email}</p>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-500">
                  <span className="flex items-center">
                    <i className="ri-graduation-cap-line mr-1"></i>
                    <span className="hidden sm:inline">{user.university}</span>
                  </span>
                  <span className="flex items-center">
                    <i className="ri-calendar-line mr-1"></i>
                    Joined {user.joinedDate}
                  </span>
                  <span className="flex items-center">
                    <i className="ri-phone-line mr-1"></i>
                    {user.phone}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button className="bg-blue-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  Edit Profile
                </button>
                <button 
                  onClick={handleLogout}
                  className="bg-gray-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
              <div className="flex items-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <i className="ri-heart-line text-red-600 text-lg md:text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Favorite Items</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{user.favoriteItems}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
              <div className="flex items-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-green-600 text-lg md:text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Purchases</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{user.purchaseHistory}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
              <div className="flex items-center">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <i className="ri-search-line text-purple-600 text-lg md:text-xl"></i>
                </div>
                <div className="ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Saved Searches</p>
                  <p className="text-xl md:text-2xl font-bold text-gray-900">{user.savedSearches}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/wallet')}
                  className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base"
                >
                  <i className="ri-wallet-line text-blue-500 mr-3"></i>
                  <span>Buyer Wallet</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-heart-line text-red-500 mr-3"></i>
                  <span>View Favorites</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-history-line text-blue-500 mr-3"></i>
                  <span>Purchase History</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-search-line text-purple-500 mr-3"></i>
                  <span>Saved Searches</span>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Account Settings</h2>
              <div className="space-y-3">
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-user-settings-line text-gray-500 mr-3"></i>
                  <span>Edit Profile</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-notification-line text-yellow-500 mr-3"></i>
                  <span>Notifications</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-shield-check-line text-green-500 mr-3"></i>
                  <span>Privacy & Security</span>
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  // Seller Profile
  if (userType === 'seller') {
    const user = userData.seller;
    
    return (
      <div className="min-h-screen bg-gray-50">
        <CampusHeader />
        
        <main className="max-w-6xl mx-auto px-4 py-6 md:py-8">
          {/* Profile Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 md:p-8 mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-teal-100 rounded-full flex items-center justify-center">
                <span className="text-lg md:text-2xl font-bold text-teal-600">{user.avatar}</span>
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h1 className="text-xl md:text-3xl font-bold text-gray-900">{user.name}</h1>
                  {user.isVerified && (
                    <i className="ri-shield-check-fill text-green-500 text-lg md:text-xl"></i>
                  )}
                </div>
                <p className="text-gray-600 mb-2 text-sm md:text-base">{user.email}</p>
                <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-gray-500">
                  <span className="flex items-center">
                    <i className="ri-graduation-cap-line mr-1"></i>
                    <span className="hidden sm:inline">{user.university}</span>
                    <span className="sm:hidden">FUTA</span>
                  </span>
                  <span className="flex items-center">
                    <i className="ri-calendar-line mr-1"></i>
                    Joined {user.joinedDate}
                  </span>
                  <span className="flex items-center">
                    <i className="ri-star-fill text-yellow-400 mr-1"></i>
                    {user.rating} rating
                  </span>
                  <span className="flex items-center">
                    <i className="ri-time-line mr-1"></i>
                    Responds {user.responseTime}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <button className="bg-teal-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer text-sm">
                  Edit Profile
                </button>
                <button 
                  onClick={handleLogout}
                  className="bg-gray-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap cursor-pointer text-sm"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 mb-6 md:mb-8">
            <div className="bg-white rounded-lg p-3 md:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-2 md:mb-0">
                  <i className="ri-store-line text-teal-600 text-sm md:text-xl"></i>
                </div>
                <div className="md:ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Active Listings</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900">{activeListingsCount}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 md:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-green-100 rounded-lg flex items-center justify-center mb-2 md:mb-0">
                  <i className="ri-shopping-bag-line text-green-600 text-sm md:text-xl"></i>
                </div>
                <div className="md:ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Total Sales</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900">{user.totalSales}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 md:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-2 md:mb-0">
                  <i className="ri-money-dollar-circle-line text-blue-600 text-sm md:text-xl"></i>
                </div>
                <div className="md:ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Total Earnings</p>
                  <p className="text-sm md:text-xl font-bold text-gray-900">{formatPrice(user.totalEarnings)}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg p-3 md:p-6 shadow-sm">
              <div className="flex flex-col md:flex-row md:items-center">
                <div className="w-8 h-8 md:w-12 md:h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-2 md:mb-0">
                  <i className="ri-star-fill text-yellow-600 text-sm md:text-xl"></i>
                </div>
                <div className="md:ml-4">
                  <p className="text-xs md:text-sm text-gray-600">Rating</p>
                  <p className="text-lg md:text-2xl font-bold text-gray-900">{user.rating}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Seller Actions</h2>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/wallet')}
                  className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base"
                >
                  <i className="ri-wallet-line text-green-500 mr-3"></i>
                  <span>Seller Wallet</span>
                </button>
                <button onClick={() => navigate('/sell')} className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-add-line text-teal-500 mr-3"></i>
                  <span>Post New Item</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-list-check text-blue-500 mr-3"></i>
                  <span>Manage Listings</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-message-line text-green-500 mr-3"></i>
                  <span>Messages</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-bar-chart-line text-purple-500 mr-3"></i>
                  <span>Analytics</span>
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold text-gray-900 mb-4">Account Settings</h2>
              <div className="space-y-3">
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-user-settings-line text-gray-500 mr-3"></i>
                  <span>Edit Profile</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-bank-card-line text-yellow-500 mr-3"></i>
                  <span>Payment Settings</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-notification-line text-orange-500 mr-3"></i>
                  <span>Notifications</span>
                </button>
                <button className="w-full flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer text-sm md:text-base">
                  <i className="ri-shield-check-line text-green-500 mr-3"></i>
                  <span>Privacy & Security</span>
                </button>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    );
  }

  return null;
};

export default ProfilePage;